<template>
  <Dialog
    v-model:visible="displayModal"
    :modal="true"
    class="section-selection-modal"
    style="width:35vw; max-width: 800px;"
    @update:visible="updateDialogVisibility"
  >
    <template #header>
      <div>
        <h3>{{ t('templateBuilder.saveSyncElementModal.selectSections') }}</h3>
        <p class="template-name">{{ templateName }}</p>
      </div>
    </template>

    <div class="section-selection-content">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-container">
        <ProgressSpinner
          style="width: 40px; height: 40px"
          stroke-width="6"
          fill="var(--surface-ground)"
          animation-duration="1.0s"
          aria-label="Loading sections"
        />
        <p class="mt-2">{{ t('templateBuilder.saveSyncElementModal.loadingSections') }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="sections.length === 0" class="empty-state">
        <i class="far fa-layer-group fa-3x text-muted mb-3"></i>
        <p class="text-muted">{{ t('templateBuilder.saveSyncElementModal.noSectionsFound') }}</p>
      </div>

      <!-- Sections List -->
      <div v-else class="sections-container">
        <div class="selection-info mb-3">
          <div class="selection-counter">
            <strong>{{ selectedSections.size }}</strong> {{ t('templateBuilder.saveSyncElementModal.sectionsSelected', { count: selectedSections.size }) }}
          </div>
          <div class="selection-actions">
            <Button
              v-if="selectedSections.size > 0"
              :label="t('templateBuilder.saveSyncElementModal.deselectAllSections')"
              icon="far fa-square"
              class="p-button-text p-button-sm"
              @click="deselectAllSections"
            />
          </div>
        </div>

        <div class="sections-grid">
          <div
            v-for="section in sections"
            :key="section.id"
            class="section-item"
            :class="{ 
              'selected': selectedSections.has(section.id),
              'disabled': !!section.syncId 
            }"
            @click="!section.syncId ? handleSectionClick(section.id) : null"
          >
            <div v-if="!section.syncId" class="section-checkbox" @click.stop>
              <input
                type="checkbox"
                :checked="selectedSections.has(section.id)"
                class="checkbox-input"
                style="display: none;"
              >
              <div
                class="checkbox-label"
                @click="handleSectionClick(section.id)"
              />
            </div>
            
            <!-- Sync indicator -->
            <Chip
              v-if="section.syncId"
              :label="section.syncName"
              icon="fas fa-arrows-repeat"
              class="sync-indicator"
            />
            <div class="section-preview">
              <div class="section-preview-container">
                <TemplateElementsPreviewImage
                  :item-id="section.id"
                  :id-shop="UserState.activeShop?.id ?? 0"
                  :fallback-text="section.syncName || section.id"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        :label="t('cancel')"
        class="p-button p-button-secondary"
        icon="far fa-times"
        @click="updateDialogVisibility"
      />
      <Button
        :label="t('templateBuilder.saveSyncElementModal.validateSelection')"
        class="p-button p-button-success"
        icon="far fa-check"
        @click="validateSelection"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Chip from 'primevue/chip';
import ProgressSpinner from 'primevue/progressspinner';
import TemplateElementsPreviewImage from '@/components/template-builder/TemplateElementsPreviewImage.vue';
import { UserState } from '@/composables/User';

interface TemplateSection {
  id: string;
  syncId?: string;
  syncName?: string;
}

export default defineComponent({
  name: 'SectionSelectionModal',

  components: {
    Dialog,
    Button,
    ProgressSpinner,
    TemplateElementsPreviewImage,
    Chip,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    templateName: {
      type: String,
      required: true,
    },
    templateConfig: {
      type: String,
      required: true,
    },
    initialSelectedSections: {
      type: Array as () => string[],
      default: () => [],
    },
  },

  emits: ['update:visible', 'sections-selected'],

  setup(props, { emit }) {
    const { t } = useI18n();
    
    const displayModal = ref(props.visible);
    const isLoading = ref(false);
    const sections = ref<TemplateSection[]>([]);
    const selectedSections = ref<Set<string>>(new Set());

    // Track if modal has been initialized to avoid resetting selection
    const isInitialized = ref(false);

    // Watch for prop changes
    watch(() => props.visible, (newValue) => {
      displayModal.value = newValue;
      if (newValue && !isInitialized.value) {
        loadSections();
        // Initialize selected sections from props only on first open
        selectedSections.value = new Set(props.initialSelectedSections);
        isInitialized.value = true;
      } else if (newValue && isInitialized.value) {
        // Just load sections without resetting selection
        loadSections();
      } else if (!newValue) {
        // Reset flag when modal is closed
        isInitialized.value = false;
      }
    });

    const loadSections = async () => {
      isLoading.value = true;
      sections.value = [];
      
      try {
        if (props.templateConfig) {
          const config = JSON.parse(props.templateConfig);
          
          if (config.body_structure && config.body_structure.sections) {
            sections.value = config.body_structure.sections.map((section: any) => ({
              id: section.id_template_elements,
              syncId: section.sync_id,
              syncName: section.sync_name,
            }));
          }
        }
      } catch (error) {
        sections.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const updateDialogVisibility = () => {
      displayModal.value = false;
      emit('update:visible', false);
    };

    const toggleSectionSelection = (sectionId: string) => {
      const newSet = new Set(selectedSections.value);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      selectedSections.value = newSet;
    };

    const handleSectionClick = (sectionId: string) => {
      toggleSectionSelection(sectionId);
    };

    const deselectAllSections = () => {
      selectedSections.value.clear();
    };

    const validateSelection = () => {
      const selectedIds = Array.from(selectedSections.value);
      emit('sections-selected', selectedIds);
      updateDialogVisibility();
    };

    return {
      t,
      displayModal,
      isLoading,
      sections,
      selectedSections,
      updateDialogVisibility,
      toggleSectionSelection,
      handleSectionClick,
      deselectAllSections,
      validateSelection,
      UserState,
    };
  },
});
</script>

<style scoped lang="scss">
.section-selection-modal {
  .template-name {
    margin: 0;
    color: #6c757d;
    font-size: 0.9rem;
    font-weight: normal;
  }

  .section-selection-content {
    min-height: 300px;
    
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      
      p {
        margin: 0;
        color: #6c757d;
      }
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      
      i {
        display: block;
        margin-bottom: 1rem;
      }
      
      p {
        margin: 0;
        font-size: 1.1rem;
      }
    }

    .sections-container {
      .selection-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        padding: 1rem;
        gap: 1rem;
        flex-wrap: wrap;
        
        .selection-counter {
          font-size: 1rem;
          color: #495057;
          
          strong {
            color: #2c3e50;
            font-size: 1.1rem;
          }
        }
        
        .selection-actions {
          display: flex;
          gap: 0.5rem;
        }
      }

      .sections-grid {
        display: grid;
        gap: 1rem;
        
        .section-item {
          position: relative;
          border: 2px solid #d3d3d3;
          border-radius: 8px;
          cursor: pointer;
          transition: border-color 0.3s ease, opacity 0.3s ease;
          
          &:hover:not(.disabled) {
            border-color: #94c840;
          }
          
          &.selected {
            border-color: #94c840;
          }
          
          &.disabled {
            border-color: #94c840;
            cursor: default;
            
            &:hover {
              border-color: #94c840;
            }
          }
          
          .section-checkbox {
            position: absolute;
            top: 10px;
            left: 10px;
            z-index: 10;
            
            .checkbox-input {
              display: none;
            }
            
            .checkbox-label {
              display: block;
              width: 24px;
              height: 24px;
              background-color: rgba(255, 255, 255, 0.9);
              border: 2px solid #ddd;
              border-radius: 4px;
              cursor: pointer;
              transition: all 0.3s ease;
              position: relative;
              
              &:hover {
                border-color: #94c840;
                background-color: rgba(148, 200, 64, 0.1);
              }
              
              &::after {
                content: '';
                position: absolute;
                top: 2px;
                left: 6px;
                width: 6px;
                height: 12px;
                border: solid white;
                border-width: 0 2px 2px 0;
                transform: rotate(45deg);
                opacity: 0;
                transition: opacity 0.3s ease;
              }
            }
            
            .checkbox-input:checked + .checkbox-label {
              background-color: #94c840;
              border-color: #94c840;
              
              &::after {
                opacity: 1;
              }
            }
          }
          
          .sync-indicator {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 10;
            font-size: 12px;
          }
          
          .section-preview {
            .section-preview-container {
              width: 100%;
              height: 200px;
              background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
              border-radius: 8px;
              overflow: hidden;
            }
          }
        }
      }
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .section-selection-modal {
    .section-selection-content {
      .sections-container {
        .selection-info {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.75rem;
          
          .selection-actions {
            align-self: stretch;
            justify-content: center;
          }
        }
        
        .sections-grid {
          grid-template-columns: 1fr;
        }
      }
    }
  }
}
</style> 